import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// State + API handlers for the admin members management page.
export function useAdminMembers(initialAlianca) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("pending");
  const [activationDialog, setActivationDialog] = useState({ open: false, link: "", memberName: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [cieesRoles, setCieesRoles] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [aliancaFilter, setAliancaFilter] = useState(initialAlianca); // 'all' | 'with' | 'without'

  const load = async () => {
    setLoading(true);
    try {
      const [r, rolesRes, commRes] = await Promise.all([
        axios.get(`${API}/admin/members`, { withCredentials: true }),
        axios.get(`${API}/admin/members/ciees-roles`, { withCredentials: true }).catch(() => ({ data: { roles: [] } })),
        axios.get(`${API}/admin/communities`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(r.data.items || []);
      setCieesRoles(rolesRes.data?.roles || []);
      setCommunities((commRes.data?.items || []).filter((c) => c.active));
    } catch (e) {
      toast.error("Erro a carregar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const lc = q.toLowerCase();
    return items.filter((m) => {
      if (tab !== "all" && m.status !== tab) return false;
      if (aliancaFilter === "with" && !m.has_alianca_amor) return false;
      if (aliancaFilter === "without" && m.has_alianca_amor) return false;
      if (!q) return true;
      return [m.email, m.name].some((s) => String(s ?? "").toLowerCase().includes(lc));
    });
  }, [items, q, tab, aliancaFilter]);

  const counts = useMemo(() => ({
    pending: items.filter((m) => m.status === "pending").length,
    approved: items.filter((m) => m.status === "approved").length,
    rejected: items.filter((m) => m.status === "rejected").length,
    all: items.length,
  }), [items]);

  const act = async (id, action) => {
    if (action === "reject" && !window.confirm("Rejeitar este pedido de adesão?")) return;
    if (action === "reinstate" && !window.confirm("Reabilitar este sócio? Volta ao estado 'Pendente' para nova análise.")) return;
    try {
      const r = await axios.post(`${API}/admin/members/${id}/${action}`, {}, { withCredentials: true });
      if (action === "approve") {
        if (r.data?.email_sent === false) {
          toast.warning("Sócio aprovado — mas o email FALHOU. Use o link manual abaixo.", { duration: 8000 });
        } else {
          toast.success("Sócio aprovado · email de boas-vindas enviado.");
        }
        // Always open the manual activation link dialog so the admin can
        // share via WhatsApp/SMS regardless of email-sent state.
        if (r.data?.activation_url) {
          const member = items.find((m) => m.id === id);
          setActivationDialog({
            open: true,
            link: r.data.activation_url,
            memberName: member?.name || "",
          });
        }
      } else {
        const messages = {
          reject: "Pedido rejeitado.",
          reinstate: "Sócio reabilitado (pendente).",
        };
        toast.success(messages[action] || "Atualizado.");
      }
      load();
    } catch (e) {
      toast.error("Erro.");
    }
  };

  const getActivationLink = async (m) => {
    try {
      const r = await axios.post(
        `${API}/admin/members/${m.id}/activation-link`,
        {},
        { withCredentials: true },
      );
      setActivationDialog({ open: true, link: r.data.activation_url, memberName: m.name || "" });
    } catch (e) {
      toast.error("Erro a gerar link.");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Apagar definitivamente o registo de ${m.name || m.email}? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/admin/members/${m.id}`, { withCredentials: true });
      toast.success("Registo apagado.");
      load();
    } catch (e) {
      toast.error("Erro a apagar.");
    }
  };

  const setTier = async (m, tier) => {
    try {
      await axios.patch(`${API}/admin/members/${m.id}/tier`, { tier: tier || null }, { withCredentials: true });
      toast.success(tier ? "Nível atribuído." : "Nível removido.");
      load();
    } catch (e) {
      toast.error("Erro a atribuir nível.");
    }
  };

  const setJoinedAt = async (m, joined_at) => {
    try {
      await axios.patch(`${API}/admin/members/${m.id}/joined-at`, { joined_at: joined_at || null }, { withCredentials: true });
      toast.success("Data de admissão atualizada.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a atualizar data.");
    }
  };

  const setCieesRole = async (m, ciees_role) => {
    try {
      await axios.patch(`${API}/admin/members/${m.id}/ciees-role`, { ciees_role: ciees_role || null }, { withCredentials: true });
      toast.success(ciees_role ? "Função CIEES atribuída." : "Função CIEES removida.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a atribuir função.");
    }
  };

  const setCommunity = async (m, community_id) => {
    try {
      await axios.patch(`${API}/admin/members/${m.id}/community`,
        { community_id: community_id || null }, { withCredentials: true });
      toast.success(community_id ? "Comunidade atribuída." : "Comunidade removida.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a atribuir comunidade.");
    }
  };

  const resendApproval = async (m) => {
    if (!window.confirm(`Re-enviar email de aprovação para ${m.email}?`)) return;
    try {
      await axios.post(`${API}/admin/members/${m.id}/resend-approval`, {}, { withCredentials: true });
      toast.success("Email re-enviado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a re-enviar.");
    }
  };

  return {
    items, loading, q, setQ, tab, setTab,
    activationDialog, setActivationDialog, importOpen, setImportOpen,
    cieesRoles, communities, aliancaFilter, setAliancaFilter,
    filtered, counts,
    load, act, getActivationLink, remove, setTier, setJoinedAt,
    setCieesRole, setCommunity, resendApproval,
  };
}
